import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Typography } from "antd";
import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useTranslate,
} from "@refinedev/core";
import { LaunchRow, Skeleton } from "@libertify/ui";
import type { ProjectAddOnSettingsPatchRequest } from "pages/media/types";
import { useProjectInstance } from "pages/media/projects/hooks/useProjectInstance";
import { PlaybookAddOnCard } from "../PlaybookAddOnCard";
import { CHAT_ADDON_ID, getConfigItemVisual } from "../../data/addons";
import type { ProjectAddOnsResponse } from "../../data/addonApi";
import { CHATBOT_DEFAULTS } from "./defaults";
import type { ChatbotFormValues } from "./types";
import { ChatbotOverview } from "./chatbot/ChatbotOverview";
import { ChatbotSettingsForm } from "./chatbot/ChatbotSettingsForm";

const KEY = "experienceEdit.chatCard";

/** Same icon and accent the rail gives this item. */
const chatVisual = getConfigItemVisual(CHAT_ADDON_ID);

export interface ChatbotEditCardProps {
  /** Whether the chat is shown to viewers of the experience. */
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  visiblePending?: boolean;
}

/**
 * Chat card for the experience edit page. Where `ChatbotConfigCard` collects
 * settings in the draft, this one reads them off the project and saves each
 * change. Generation is a separate call, so saving here regenerates nothing.
 */
export const ChatbotEditCard = ({
  visible,
  onVisibleChange,
  visiblePending,
}: ChatbotEditCardProps) => {
  const t = useTranslate();
  const { projectId, instanceId } = useParams<{
    projectId: string;
    instanceId?: string;
  }>();
  const navigate = useNavigate();
  const apiUrl = useApiUrl();
  const addOnsUrl = `${apiUrl}/media/projects/${projectId}/add-ons`;

  // Same read the page makes for its rail, so this shares its cache.
  const { data, isLoading, refetch } = useCustom<ProjectAddOnsResponse>({
    url: addOnsUrl,
    method: "get",
    queryOptions: { enabled: Boolean(projectId) },
  });

  // Mirrors ExperienceEditPage and GeneralSettingsEditCard: the route's
  // :instanceId is optional, and "default" is the instance it falls back to.
  const { markAsOutOfDate } = useProjectInstance({
    projectId: projectId ?? "",
    instanceId: instanceId ?? "default",
  });

  const { mutateAsync } = useCustomMutation();
  const save = async (
    settings: ChatbotFormValues["functional_settings"]
  ): Promise<boolean> => {
    try {
      await mutateAsync({
        url: `${addOnsUrl}/settings`,
        method: "patch",
        values: {
          add_on_type: CHAT_ADDON_ID,
          functional_settings: settings,
        } satisfies ProjectAddOnSettingsPatchRequest,
        successNotification: false,
      });
    } catch {
      // useCustomMutation raises the error notification itself.
      return false;
    }
    // useCustomMutation touches no cache, and the card remounts on every rail
    // switch — without this it reseeds from the pre-save read and the next
    // change PATCHes those stale values back over this one.
    await refetch();
    // A live publication was built from the settings we just replaced, so it
    // needs regenerating. No-ops when nothing is live. Kept off the success
    // path: the settings are saved either way, and it notifies on its own.
    await markAsOutOfDate().catch(() => undefined);
    return true;
  };

  const stored = data?.data.settings.chatbot_functional_settings;
  const initialValues: ChatbotFormValues = {
    functional_settings: { ...CHATBOT_DEFAULTS.functional_settings, ...stored },
  };

  return (
    <PlaybookAddOnCard
      itemId={CHAT_ADDON_ID}
      title={t(`${KEY}.title`)}
      description={t(`${KEY}.subtitle`)}
      visible={visible}
      visiblePending={visiblePending}
      onVisibleChange={onVisibleChange}
    >
      <Flex vertical gap={24}>
        <ChatbotOverview projectId={projectId} />
        <Flex vertical gap={2}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t(`${KEY}.settings.heading`)}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t(`${KEY}.settings.subtitle`)}
          </Typography.Text>
        </Flex>
        {/* Mounted once the settings are in, so the form seeds from them. */}
        {isLoading && projectId ? (
          <Skeleton variant="text" rows={6} />
        ) : (
          <ChatbotSettingsForm initialValues={initialValues} onSave={save} />
        )}
        <LaunchRow
          icon={<chatVisual.Icon weight={chatVisual.weight} />}
          color={chatVisual.color}
          title={t(`${KEY}.insights.title`)}
          description={t(`${KEY}.insights.description`)}
          actionLabel={t(`${KEY}.insights.action`)}
          disabled={!projectId}
          onAction={() =>
            navigate(`/media/projects/${projectId}/analytics/ai-chatbot`)
          }
        />
      </Flex>
    </PlaybookAddOnCard>
  );
};

export default ChatbotEditCard;
